import React, { useState } from "react";
import { useLeadTimeStats } from './hooks/useLeadTimeStats';
import BaseTile from '../../../components/Tile/BaseTile';
import TileContent, { TileStatusContent, TileGridContent } from '../../../components/Tile/TileContent';

// StatCard Component
const StatCard = ({ title, value, unit = '', icon }) => (
  <div className="bg-gray-50 p-4 rounded-lg flex-1 min-w-[150px] text-center">
    <div className="flex flex-col items-center justify-center">
      {icon && (
        <div className="mb-2 text-primary-500">
          {icon}
        </div>
      )}
      <h4 className="text-sm font-medium text-gray-600 mb-2 leading-tight">
        {title}
      </h4>
      <p className="text-2xl font-bold text-gray-800">
        {value !== null && value !== undefined ? (
          <>
            {typeof value === 'number' ? value.toLocaleString() : value}
            {unit && <span className="text-lg text-gray-600 ml-1">{unit}</span>}
          </>
        ) : (
          <span className="text-gray-400">—</span>
        )}
      </p>
    </div>
  </div>
);

// Common object types - can be extended based on your dataset
const OBJECT_TYPES = [
  { value: 'Auftrag', label: 'Auftrag' },
  { value: 'Angebot', label: 'Angebot' },
  { value: 'Lieferschein', label: 'Lieferschein' },
];

const LeadTimeDashboard = ({ activeFile }) => {
  const [selectedObjectType, setSelectedObjectType] = useState('Auftrag');
  const fileType = activeFile?.type || 'main';
  const { stats, loading, error, hasFetched, fetchStats } = useLeadTimeStats(fileType, selectedObjectType);

  // Determine what to show
  const showInitialButton = !hasFetched && !loading;
  const showStatsGrid = hasFetched && !loading && !error && stats;
  const showNoDataMessage = hasFetched && !loading && !error && !stats;

  return (
    <BaseTile
      title={
        <div className="flex items-center gap-3">
          <span>Lead Time Statistics</span>
          <select
            value={selectedObjectType}
            onChange={(e) => setSelectedObjectType(e.target.value)}
            className="px-3 py-1 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {OBJECT_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>
      }
      loading={loading}
      error={error}
      minHeight="lg"
      className="h-full"
    >
      <TileContent layout="flex" direction="column" grow>
        {/* Initial Load Button */}
        {showInitialButton && (
          <TileStatusContent
            status="empty"
            title="Ready to Load Statistics"
            message="Click the button below to load lead time statistics."
            action={
              <button
                onClick={fetchStats}
                disabled={loading}
                className="px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              >
                Load Lead Time Stats
              </button>
            }
          />
        )}

        {/* Stats Grid */}
        {showStatsGrid && (
          <div className="flex justify-around flex-wrap gap-4 py-4">
            <StatCard
              title="Min Lead Time"
              value={stats.min !== null ? stats.min.toFixed(1) : null}
              unit="d"
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                </svg>
              }
            />
            <StatCard
              title="Max Lead Time"
              value={stats.max !== null ? stats.max.toFixed(1) : null}
              unit="d"
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              }
            />
            <StatCard
              title="Ø Lead Time"
              value={stats.average !== null ? stats.average.toFixed(1) : null}
              unit="d"
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              }
            />
            <StatCard
              title="Total Count"
              value={stats.count}
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                </svg>
              }
            />
          </div>
        )}

        {/* No Data State */}
        {showNoDataMessage && (
          <TileStatusContent
            status="empty"
            title="No Statistics Available"
            message="Could not load lead time statistics."
            action={
              <button
                onClick={fetchStats}
                className="px-4 py-2 bg-gray-500 text-white text-sm font-medium rounded-md hover:bg-gray-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Retry Load
              </button>
            }
          />
        )}

        {/* Error State with Retry */}
        {error && hasFetched && (
          <div className="flex justify-center mt-4">
            <button
              onClick={fetchStats}
              className="px-4 py-2 bg-gray-500 text-white text-sm font-medium rounded-md hover:bg-gray-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              Retry Load
            </button>
          </div>
        )}
      </TileContent>
    </BaseTile>
  );
};

export default LeadTimeDashboard;